import { addMessage, messageBodyAriaLabel, showMessageHistory } from './ComposeBody.locstring.json';
import ComposeComponentProps from './ComposeComponentProps';
import ComposeDropHint from './ComposeDropHint';
import logEditorPerf from '../utils/logEditorPerf';
import { observer } from 'mobx-react';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { logUsage } from 'owa-analytics';
import { AttachmentFullViewState } from 'owa-attachment-full-data';
import { AttachmentWellView } from 'owa-attachment-well-view';
import { ClientItemId } from 'owa-client-ids';
import { ComposeFeedback } from 'owa-compose-feedback';
import { ControlIcons } from 'owa-control-icons';
import Droppable from 'owa-dnd/lib/components/Droppable';
import { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import { LocalFilesDragData } from 'owa-dnd/lib/utils/LocalFilesDragData';
import { CompositeEditor } from 'owa-editor';
import getMentionClassName from 'owa-editor-mentions-picker-plugin/lib/utils/getMentionClassName';
import { PasteOption } from 'owa-editor-paste-option-plugin';
import { ContextualFormatBar } from 'owa-editor-ribbonplugin';
import { TableEditContextMenu } from 'owa-editor-table-edit-plugin';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc from 'owa-localize';
import { lazyCreateAttachments } from 'owa-mail-compose-actions';
import setExplicitTextDirection from 'owa-mail-compose-actions/lib/actions/setExplicitTextDirection';
import setIsFocusWorkaroundDivShown from 'owa-mail-compose-actions/lib/actions/setIsFocusWorkaroundDivShown';
import showSubjectPickerButton from 'owa-mail-compose-actions/lib/actions/showSubjectPickerButton';
import triggerPolicyTips from 'owa-mail-compose-actions/lib/actions/triggerPolicyTips';
import updateLastFocusedControl from 'owa-mail-compose-actions/lib/actions/updateLastFocusedControl';
import { getPluginArray } from 'owa-editor-plugins-list';
import { addViewStateToBeforeUnloadHandler } from 'owa-mail-compose-actions/lib/utils/beforeUnloadHandler';
import upConvertHelper from 'owa-mail-compose-actions/lib/utils/onUpconvertHelper';
import { getCurrentTableMailboxInfo } from 'owa-mail-mailboxinfo';
import { mailStore } from 'owa-mail-store';
import { MailListItemPartDragData } from 'owa-mail-types/lib/types/MailListItemPartDragData';
import { MailListRowDragData } from 'owa-mail-types/lib/types/MailListRowDragData';
import { isPolicyTipsEnabled } from 'owa-policy-tips/lib/utils/isPolicyTipsEnabled';
import EventTrigger from 'owa-service/lib/contract/EventTrigger';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { ComposeSmartPillBlock } from 'owa-smart-pill-compose';
import isSmimeEnabledInViewState from 'owa-smime/lib/utils/isSmimeEnabledInViewState';
import { DarkModeContext } from 'owa-theme';
import { isBrowserEdge, isBrowserIE } from 'owa-user-agent/lib/userAgent';
import { ComposeControl, TextDirection } from 'owa-mail-compose-store';
import * as React from 'react';

import {
    CreateAttachmentsProperties,
    lazyConvertImageViewAttachmentToInlineAttachment,
} from 'owa-attachment-well-data';
import {
    AttachmentDragData,
    AttachmentFileType,
    createLocalComputerFiles,
    createMailItemFiles,
} from 'owa-attachment-file-types';
import {
    infoBarAddHandler,
    infoBarRemoveHandler,
} from 'owa-mail-compose-actions/lib/utils/infoBarHandlers';

const styles = require('./ComposeBody.scss');
const classNames = require('classnames/bind').bind(styles);

@observer
export default class ComposeBody extends React.Component<ComposeComponentProps, {}> {
    composeWrapperDivRef: React.RefObject<Droppable> = React.createRef();
    static contextType = DarkModeContext;

    componentDidMount() {
        addViewStateToBeforeUnloadHandler(this.props.viewState);
        this.setComposeWrapperOnRoosterPlugins();
    }

    componentDidUpdate() {
        this.setComposeWrapperOnRoosterPlugins();
    }

    public trySetFocus = () => {
        this.props.plugins.contentOperationPlugin.focusToEditor();
    };

    render() {
        const { viewState } = this.props;
        const isDarkMode = this.context;
        const attachmentWell = viewState.attachmentWell;
        const hasAttachment =
            (attachmentWell.docViewAttachments && attachmentWell.docViewAttachments.length > 0) ||
            (attachmentWell.imageViewAttachments && attachmentWell.imageViewAttachments.length > 0);
        const bodyClassNames = classNames(styles.bodyContainer, {
            hasAttachment: hasAttachment,
            isInlineCompose: viewState.isInlineCompose,
            sticky: viewState.docking && viewState.docking.useStickyDocking,
            darkMode: isDarkMode,
        });
        return (
            <Droppable
                classNames={bodyClassNames}
                ref={this.composeWrapperDivRef}
                dropViewState={viewState.dropViewState}
                onDrop={this.onDrop}
                canDrop={this.canDrop}
                shouldUseCopyAsDropEffect={true}
                shouldIgnoreTransientOnDragLeave={true}>
                {this.renderAttachmentWell()}
                {this.renderFocusWorkaroundDiv()}
                {this.renderEditor(isDarkMode)}
                {this.renderFocusWorkaroundDiv()}
                {isFeatureEnabled('mc-smartPillsCompose') && this.renderSmartPillBar()}
                {this.renderContextualFormatBar()}
                {this.renderEditTableMenu()}
                {this.renderPasteOption()}
                {this.renderFeedbackRibbon()}
                {this.renderShowHistoryBar()}
                {this.renderDropHint()}
            </Droppable>
        );
    }

    private setComposeWrapperOnRoosterPlugins() {
        const { plugins } = this.props;
        const div = this.composeWrapperDivRef.current
            ? this.composeWrapperDivRef.current.getDiv()
            : null;
        if (plugins && plugins.dockingPlugin) {
            plugins.dockingPlugin.setDockingTriggerPart(div);
        }

        if (plugins.inlineImageResizePlugin) {
            plugins.inlineImageResizePlugin.addOnImportedCallback(plugin =>
                plugin.setComposeBodyWrapperDiv(div)
            );
        }
    }

    private renderDropHint(): JSX.Element {
        return (
            this.shouldShowComposeDropHint() &&
            this.props.plugins && (
                <ComposeDropHint
                    dataTransferType={mailStore.typeOfItemBeingDragged}
                    dropViewState={this.props.viewState.dropViewState}
                    dockingPlugin={this.props.plugins.dockingPlugin}
                />
            )
        );
    }

    private shouldShowComposeDropHint = (): boolean => {
        // If classic attachments are disabled then we disable drag and drop
        if (!this.classicAttachmentsEnabled()) {
            return false;
        }

        // Compose body only allows to drop MailListRow and LocalFile.
        return (
            mailStore.typeOfItemBeingDragged === DraggableItemTypes.MailListRow ||
            mailStore.typeOfItemBeingDragged === DraggableItemTypes.MultiMailListConversationRows ||
            mailStore.typeOfItemBeingDragged === DraggableItemTypes.MultiMailListMessageRows ||
            mailStore.typeOfItemBeingDragged === DraggableItemTypes.LocalFile ||
            mailStore.typeOfItemBeingDragged === DraggableItemTypes.Attachment
        );
    };

    private onAttachmentDeleted = () => {
        if (isPolicyTipsEnabled()) {
            // When attachment is deleted, check DLP information.
            triggerPolicyTips(this.props.viewState, EventTrigger.AttachmentRemoved);
        }
    };

    private onAttachmentMovedInline = (attachment: AttachmentFullViewState, contentId: string) => {
        lazyConvertImageViewAttachmentToInlineAttachment.importAndExecute(
            this.props.viewState.attachmentWell,
            attachment,
            contentId
        );
    };

    private renderAttachmentWell(): JSX.Element {
        const { viewState } = this.props;
        const parentItemId: ClientItemId = {
            ...viewState.itemId,
            mailboxInfo: getCurrentTableMailboxInfo(),
        };
        return (
            <AttachmentWellView
                attachmentWellViewState={viewState.attachmentWell}
                parentItemId={parentItemId}
                isDraft={true}
                infoBarAddHandler={infoBarAddHandler(this.props.viewState)}
                infoBarRemoveHandler={infoBarRemoveHandler(this.props.viewState)}
                onAttachmentDeletedCallback={this.onAttachmentDeleted}
                moveAttachmentToInlineHandler={
                    viewState.bodyType == 'HTML' ? this.onAttachmentMovedInline : null
                }
                onLostFocus={this.trySetFocus}
            />
        );
    }

    private isRtl() {
        return this.props.viewState.explicitTextDirection
            ? this.props.viewState.explicitTextDirection === TextDirection.RTL
            : getUserConfiguration().SessionSettings.IsUserCultureRightToLeft;
    }

    private renderEditor(isDarkMode: boolean): JSX.Element {
        const { viewState, plugins } = this.props;
        const isHtmlEditor = viewState.bodyType == 'HTML';
        const editorClassNames = classNames(
            styles.editorBody,
            viewState.subjectPickerViewState.isBackButtonHovered && styles.fadedOut,
            {
                isInlineCompose: viewState.isInlineCompose,
                hasRibbon: viewState.isRibbonShown,
                plainTextEditor: !isHtmlEditor,
                darkMode: isDarkMode,
            }
        );

        return (
            <CompositeEditor
                plugins={getPluginArray(plugins)}
                undo={plugins.undo}
                viewState={viewState}
                classNames={editorClassNames}
                onFocus={this.onEditorFocus}
                onBlur={this.onEditorBlur}
                onDirectionChange={this.onEditorDirectionChange}
                showFormatPainterCursor={!!viewState.ribbon.formatViewState.formatPainterFormat}
                isRtl={this.isRtl()}
                cssClassesToPreserveWhenPaste={[getMentionClassName()]}
                logPerfDataCallback={
                    isFeatureEnabled('cmp-editorPerfMeasure') ? logEditorPerf : null
                }
                ariaLabel={loc(messageBodyAriaLabel)}
                isDarkMode={isDarkMode}
                plainTextPlaceholder={loc(addMessage)}
                onPlainTextContentChange={this.onPlainTextChange}
                plainTextEditorRefCallback={this.plainTextEditorRefCallback}
            />
        );
    }

    private plainTextEditorRefCallback = (ref: HTMLTextAreaElement) => {
        if (this.props.plugins.contentOperationPlugin) {
            this.props.plugins.contentOperationPlugin.setPlainTextEditor(ref);
        }
        if (this.props.plugins.addinPlugin) {
            this.props.plugins.addinPlugin.setPlainTextEditor(ref);
        }
    };

    private renderContextualFormatBar(): JSX.Element {
        return (
            <ContextualFormatBar
                className={styles.contextualFormatBar}
                ribbonPlugin={this.props.plugins.ribbonPlugin}
                viewState={this.props.viewState.ribbon}
                isRtl={this.isRtl()}
            />
        );
    }

    private renderPasteOption(): JSX.Element {
        const { pasteOptionPlugin } = this.props.plugins;
        return (
            pasteOptionPlugin && (
                <PasteOption
                    viewState={this.props.viewState.pasteOption}
                    plugin={pasteOptionPlugin}
                />
            )
        );
    }

    private renderFeedbackRibbon(): JSX.Element {
        const { composeFeedbackViewState } = this.props.viewState;
        return (
            composeFeedbackViewState.shouldShowFeedbackRibbon && (
                <ComposeFeedback viewState={composeFeedbackViewState} />
            )
        );
    }

    private renderShowHistoryBar(): JSX.Element {
        const { viewState } = this.props;
        return (
            viewState.isInlineCompose && (
                <IconButton
                    title={loc(showMessageHistory)}
                    ariaLabel={loc(showMessageHistory)}
                    onClick={this.onUpConvert}
                    className={styles.showHistoryButton}
                    iconProps={{
                        iconName: ControlIcons.More,
                        styles: {
                            root: styles.showHistoryButtonIcon,
                        },
                    }}
                />
            )
        );
    }

    private renderEditTableMenu(): JSX.Element {
        return (
            <TableEditContextMenu
                viewState={this.props.viewState.tableEdit}
                plugin={this.props.plugins.tableEdit}
            />
        );
    }

    private renderSmartPillBar(): JSX.Element {
        return <ComposeSmartPillBlock />;
    }

    private onPlainTextChange = () => {
        this.props.plugins.contentOperationPlugin.updateContentToViewState();
    };

    private onUpConvert = async () => {
        upConvertHelper(this.props.viewState);
    };

    private onDrop = async (dragInfo: DragData) => {
        let attachmentFileList = null;
        let shouldShare = false;
        // Attachment sizes will be updated by the server in non-S/MIME mode
        const shouldUseSizes = isSmimeEnabledInViewState(this.props.viewState.smimeViewState);
        switch (dragInfo.itemType) {
            case DraggableItemTypes.LocalFile:
                const filesDragData = dragInfo as LocalFilesDragData;
                attachmentFileList = createLocalComputerFiles(filesDragData.files);
                break;
            case DraggableItemTypes.MailListRow:
            case DraggableItemTypes.MultiMailListConversationRows:
            case DraggableItemTypes.MultiMailListMessageRows:
                const mailListItemsDragData = dragInfo as MailListRowDragData;
                attachmentFileList = createMailItemFiles(
                    mailListItemsDragData.subjects,
                    mailListItemsDragData.latestItemIds,
                    shouldUseSizes ? mailListItemsDragData.sizes : []
                );
                break;
            case DraggableItemTypes.MailListItemPart:
                const mailListItemPartDragData = dragInfo as MailListItemPartDragData;
                attachmentFileList = createMailItemFiles(
                    mailListItemPartDragData.subjects,
                    mailListItemPartDragData.itemIds,
                    shouldUseSizes ? mailListItemsDragData.sizes : []
                );
                break;
            case DraggableItemTypes.Attachment:
                const attachmentDragData = dragInfo as AttachmentDragData;
                attachmentFileList = [attachmentDragData.attachmentFile];
                shouldShare =
                    attachmentDragData.attachmentFile.fileType === AttachmentFileType.Cloud ||
                    attachmentDragData.attachmentFile.fileType ===
                        AttachmentFileType.CloudSuggestion ||
                    attachmentDragData.attachmentFile.fileType ===
                        AttachmentFileType.ContextualSuggestion;
                break;
            default:
                break;
        }

        if (attachmentFileList) {
            logUsage('AttachmentDragAndDrop', [dragInfo.itemType]);
            const createAttachments = await lazyCreateAttachments.import();
            const baseProperties = { isInline: false, isHiddenInline: false };
            const properties: CreateAttachmentsProperties = shouldShare
                ? { ...baseProperties, shouldShare: true }
                : { ...baseProperties, shouldShare: false };
            createAttachments(attachmentFileList, this.props.viewState, properties, null);
        }
    };

    private canDrop = (dragInfo: DragData) => {
        // If classic attachments are disabled then we disable drag and drop
        if (!this.classicAttachmentsEnabled()) {
            return false;
        }

        const itemType = dragInfo.itemType;
        return (
            itemType === DraggableItemTypes.LocalFile ||
            itemType === DraggableItemTypes.MailListItemPart ||
            itemType === DraggableItemTypes.MailListRow ||
            itemType === DraggableItemTypes.MultiMailListConversationRows ||
            itemType === DraggableItemTypes.MultiMailListMessageRows ||
            itemType === DraggableItemTypes.Attachment
        );
    };

    // Workaround a behavior in Edge/IE
    // Add an extra DIV before and after editor to accept the focus by press TAB/Shift+Tab then use code to trigger the real focus into editor
    // So that the view will not do auto scroll
    private renderFocusWorkaroundDiv(): JSX.Element {
        return (
            (isBrowserEdge() || isBrowserIE()) &&
            this.props.viewState.isFocusWorkaroundDivShown && (
                <div onFocus={this.trySetFocus} tabIndex={0} />
            )
        );
    }

    private onEditorFocus = () => {
        setIsFocusWorkaroundDivShown(this.props.viewState, false /*isShown*/);
        showSubjectPickerButton(this.props.viewState, false);
        updateLastFocusedControl(this.props.viewState, ComposeControl.Editor);
    };

    private onEditorBlur = () => {
        setIsFocusWorkaroundDivShown(this.props.viewState, true /*isShown*/);
    };

    private onEditorDirectionChange = (editorDirection: 'ltr' | 'rtl') => {
        if (this.props.viewState.explicitTextDirection !== editorDirection) {
            setExplicitTextDirection(this.props.viewState, editorDirection as TextDirection);
        }
    };

    private classicAttachmentsEnabled = () => {
        const userConfiguration = getUserConfiguration();
        return userConfiguration.AttachmentPolicy.ClassicAttachmentsEnabled;
    };
}
